import { useParams, Link, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchClient, fetchClientAreas } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Gauge, Briefcase, Megaphone, Search as SearchIcon, Cpu, Lock, MousePointer, Sparkles, ClipboardList, FileText } from "lucide-react";
import OverviewTab from "@/pages/client-tabs/OverviewTab";
import ReportsTab from "@/pages/client-tabs/ReportsTab";
import CommunicationsTab from "@/pages/client-tabs/CommunicationsTab";
import SeoGeoTab from "@/pages/client-tabs/SeoGeoTab";
import CroTab from "@/pages/client-tabs/CroTab";
import BdTab from "@/pages/client-tabs/BdTab";
import ComingSoonTab from "@/pages/client-tabs/ComingSoonTab";
import GeoIntelTab from "@/pages/client-tabs/geo-intel/GeoIntelTab";

const ICONS = { Gauge, Briefcase, Megaphone, Search: SearchIcon, Cpu, MousePointer, Sparkles, ClipboardList, FileText };

const STATUS_TONE = {
  on_track: { dot: "bg-emerald-500", text: "text-emerald-700", label: "On track" },
  in_progress: { dot: "bg-sky-500", text: "text-sky-700", label: "En proceso" },
  at_risk: { dot: "bg-amber-500", text: "text-amber-700", label: "En riesgo" },
  behind: { dot: "bg-rose-500", text: "text-rose-700", label: "Atrasado" },
  info: { dot: "bg-slate-400", text: "text-slate-600", label: "Activo" },
  pending: { dot: "bg-slate-300", text: "text-slate-500", label: "Por configurar" },
};

export default function ClientDetail() {
  const { id } = useParams();
  const { canAccessClient, user } = useAuth();
  const { data: client } = useQuery({ queryKey: ["client", id], queryFn: () => fetchClient(id), enabled: !user || canAccessClient(id) });
  const { data: areasData } = useQuery({ queryKey: ["client-areas", id], queryFn: () => fetchClientAreas(id), enabled: !user || canAccessClient(id) });

  if (user && !canAccessClient(id)) return <Navigate to="/login" replace />;

  if (!client) {
    return (
      <div className="space-y-6" data-testid="client-detail-loading">
        <div className="h-20 rounded-md skeleton" />
        <div className="h-10 rounded-md skeleton" />
        <div className="h-72 rounded-md skeleton" />
      </div>
    );
  }

  const areas = areasData?.areas || [];

  return (
    <div className="space-y-4" data-testid="client-detail-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/clients" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 font-medium" data-testid="back-to-clients-link">
          <ArrowLeft className="h-3.5 w-3.5" /> Volver a clientes
        </Link>
        <span className="text-slate-300">/</span>
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="h-6 w-6 rounded-sm flex items-center justify-center font-display font-semibold text-white text-[10px] shrink-0"
            style={{ background: client.color }}
            data-testid="client-avatar-mini"
          >
            {client.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
          </div>
          <span className="font-display text-sm font-semibold text-slate-900 truncate" data-testid="client-name-inline">{client.name}</span>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="w-full" data-testid="client-tabs">
        <TabsList className="h-auto p-1 bg-slate-100 border border-slate-200 rounded-md flex flex-wrap gap-1">
          {areas.map((a) => {
            const Icon = ICONS[a.icon] || Gauge;
            const tone = STATUS_TONE[a.status] || STATUS_TONE.pending;
            return (
              <TabsTrigger
                key={a.key}
                value={a.key}
                data-testid={`tab-${a.key}`}
                className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3.5 py-1.5 rounded-sm text-sm font-medium gap-2"
              >
                <Icon className="h-4 w-4" />
                <span>{a.label}</span>
                <span className={`h-1.5 w-1.5 rounded-full ${tone.dot} ml-1`} aria-hidden />
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab clientId={id} client={client} clientName={client?.name} />
        </TabsContent>

        <TabsContent value="reports" className="mt-6">
          <ReportsTab clientId={id} clientName={client?.name} />
        </TabsContent>

        <TabsContent value="business_development" className="mt-6">
          <BdTab clientId={id} />
        </TabsContent>

        <TabsContent value="communications" className="mt-6">
          <CommunicationsTab clientId={id} />
        </TabsContent>

        <TabsContent value="seo_aeo" className="mt-6">
          <SeoGeoTab clientId={id} />
        </TabsContent>

        <TabsContent value="cro" className="mt-6">
          <CroTab clientId={id} />
        </TabsContent>

        <TabsContent value="ai_visibility" className="mt-6">
          <GeoIntelTab clientId={id} clientName={client?.name} />
        </TabsContent>

        <TabsContent value="technology" className="mt-6">
          <ComingSoonTab
            title="Tecnología"
            description="Estado del stack técnico, integraciones, performance y seguridad."
            metrics={[
              "Core Web Vitals (LCP, INP, CLS)",
              "Uptime e incidentes",
              "Sprints de desarrollo entregados",
              "Deuda técnica y backlog priorizado",
            ]}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
